<template>
  <transition name="slide-down">
    <div
      v-if="store.hasAlerts && topAlert"
      role="alert"
      aria-live="polite"
      aria-atomic="true"
      class="global-alert-banner"
      :class="`level-${topAlert.level}`"
    >
      <div class="alert-content">
        <div class="alert-icon">
          <icon-exclamation-circle-fill v-if="topAlert.level === 'critical'" />
          <icon-exclamation-polygon-fill v-else-if="topAlert.level === 'warning'" />
          <icon-info-circle-fill v-else />
        </div>
        <div class="alert-text">
          <span class="alert-title">{{ topAlert.title }}</span>
          <span v-if="topAlert.detail" class="alert-detail">{{ topAlert.detail }}</span>
        </div>
        <div v-if="store.activeAlerts.length > 1" class="alert-count">
          还有 {{ store.activeAlerts.length - 1 }} 条告警
        </div>
        <router-link v-if="topAlert.route" :to="topAlert.route" class="alert-action">
          查看详情
        </router-link>
        <button class="alert-dismiss" @click="store.dismissAlert(topAlert.id)" aria-label="关闭告警">
          <icon-close />
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useAlertBannerStore } from '@/stores/alertBanner'
import {
  IconExclamationCircleFill,
  IconExclamationPolygonFill,
  IconInfoCircleFill,
  IconClose,
} from '@arco-design/web-vue/es/icon'

const store = useAlertBannerStore()

/** 当前展示的最高优先级告警（添加 undefined 保护） */
const topAlert = computed(() => store.activeAlerts[0] ?? null)
</script>

<style lang="scss" scoped>
.global-alert-banner {
  width: 100%;
  padding: 8px 24px;
  display: flex;
  align-items: center;
  z-index: 100;
  font-size: 13px;

  &.level-critical {
    background: var(--color-danger-light-1, #fff0f0);
    border-bottom: 1px solid var(--color-danger-light-3, #ffc6c6);
    color: var(--color-danger, #f53f3f);
  }

  &.level-warning {
    background: var(--color-warning-light-1, #fff7e8);
    border-bottom: 1px solid var(--color-warning-light-3, #ffe4ba);
    color: var(--color-warning, #CC785C);
  }

  &.level-info {
    background: var(--color-primary-light-1, #F4E8E2);
    border-bottom: 1px solid var(--color-primary-light-3, #bedaff);
    color: var(--color-primary, #9D5640);
  }
}

.alert-content {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
}

.alert-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.alert-text {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.alert-title {
  font-weight: 500;
}

.alert-detail {
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alert-count {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.06);
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.alert-action {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  text-decoration: none;
  color: inherit;

  &:hover {
    text-decoration: underline;
  }
}

.alert-dismiss {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: inherit;
  opacity: 0.6;
  font-size: 14px;
  display: flex;

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.06);
  }
}

// 过渡动画
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
